import { useState, useRef, useEffect, useId } from 'react';
import { CircleCheckBig } from 'lucide-react';
import { STATUS_COLORS } from '../config/colors';
import { Button } from './ui/button';

export interface LineChartDataPoint {
  label: string;
  value: number;
  weekday?: string;
  timestamp?: string;
  date?: string;
  hour?: number;
  week_start?: string;
  month?: string;
}

export interface LineChartSeries {
  name: string;
  data: LineChartDataPoint[];
  color: string;
  strokeWidth?: number;
}

interface LineChartProps {
  data?: LineChartDataPoint[];
  series?: LineChartSeries[];
  title?: string;
  heightClass?: string;
  chartHeight?: number;
  yAxisLabel?: string;
  xAxisLabel?: string;
  color?: string;
  formatValueAsInteger?: boolean;
  formatYAxisAsK?: boolean;
  defaultZoomed?: boolean;
  showWeekNavigation?: boolean;
  hasPreviousWeek?: boolean;
  hasNextWeek?: boolean;
  onPreviousWeek?: () => void;
  onNextWeek?: () => void;
  previousWeekLabel?: string;
  nextWeekLabel?: string;
  /** When set (e.g. from KPI card hover), highlight this series by name and dim others */
  highlightedSeriesName?: string | null;
  /** Format x-axis label for display (e.g. "2026-01-01" -> "Jan 1") */
  formatXLabel?: (label: string) => string;
  /** When set (e.g. 7 for daily), show only every Nth label on x-axis */
  xAxisLabelInterval?: number;
  /** When set (e.g. from Filter by Date), draw a grey dotted vertical line at this x-axis label */
  selectedXLabel?: string | null;
  /** When set (e.g. { value: 70 }), draw a horizontal dotted reference line at this y value */
  horizontalReferenceLine?: { value: number; color?: string; strokeDasharray?: string } | null;
  /** When true, do not render circles at data points (cleaner lines only) */
  hideDataPoints?: boolean;
  /** When true, hide numeric y-tick labels and the rotated y-axis title (grid lines stay) */
  hideYAxisLabels?: boolean;
  /** When set (e.g. "Net Profit"), this series gets a subtle gradient area fill under the line */
  gradientFillSeriesName?: string | null;
  /** When set (e.g. "Net Profit"), show a flickering point on the most recent (last) data point of that series */
  highlightLatestPointSeriesName?: string | null;
  /** Tighter padding and margins for embedding in dashboard tiles */
  dense?: boolean;
  /** Pin the Y axis to an exact domain (e.g. 0–100 for rates). Disables vertical zoom. */
  fixedYDomain?: { min: number; max: number } | null;
}

export default function LineChart({ data, series, title, heightClass = 'min-h-100', chartHeight = 320, yAxisLabel = 'Value', xAxisLabel, color = STATUS_COLORS.success, formatValueAsInteger = false, formatYAxisAsK = false, defaultZoomed = false, showWeekNavigation = false, hasPreviousWeek = false, hasNextWeek = false, onPreviousWeek, onNextWeek, previousWeekLabel, nextWeekLabel, highlightedSeriesName = null, formatXLabel, xAxisLabelInterval, selectedXLabel = null, horizontalReferenceLine = null, hideDataPoints = false, hideYAxisLabels = false, gradientFillSeriesName = null, highlightLatestPointSeriesName = null, dense = false, fixedYDomain = null }: LineChartProps) {
  const gradientId = useId().replace(/:/g, '');
  const redGradientId = useId().replace(/:/g, '');
  const latestPointPulseId = useId().replace(/:/g, '');
  const [hoveredPoint, setHoveredPoint] = useState<{ seriesIndex: number; pointIndex: number; x: number; y: number; value: number; name: string } | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [isVerticallyZoomed, setIsVerticallyZoomed] = useState<boolean>(defaultZoomed);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  
  // Reset zoom state when data changes
  useEffect(() => {
    setIsVerticallyZoomed(defaultZoomed);
  }, [data, series, defaultZoomed]);
  
  // Normalize single data prop to series format for consistent handling.
  // When gradientFillSeriesName is set, use it as the series name so the fill matches.
  const chartSeries: LineChartSeries[] = series || (data
    ? [{ name: gradientFillSeriesName || title || 'Data', data, color }]
    : []);
  const allDataPoints = chartSeries.flatMap(s => s.data);
  const labels = Array.from(new Set(allDataPoints.map(p => p.label)));

  // Effective highlight: from external (KPI hover) or from internal (line/legend hover). -1 = none.
  const effectiveHighlightedSeriesIndex = highlightedSeriesName != null
    ? chartSeries.findIndex(s => s.name === highlightedSeriesName)
    : (hoveredPoint?.seriesIndex ?? -1);
  // Only one series is "highlighted" when user has hovered; no glow by default
  const isSeriesHighlighted = (seriesIndex: number) =>
    effectiveHighlightedSeriesIndex >= 0 && effectiveHighlightedSeriesIndex === seriesIndex;

  // Calculate min/max values, only if we have data points
  let maxValue = 1;
  let minValue = 0;
  let valueRange = 1;
  
  if (allDataPoints.length > 0) {
    const values = allDataPoints.map(d => d.value).filter(v => v != null && !isNaN(v) && isFinite(v));
    if (values.length > 0) {
      maxValue = Math.max(...values);
      minValue = Math.min(...values);
      valueRange = maxValue - minValue;
      if (valueRange === 0) valueRange = 1; // Avoid division by zero
    }
  }
  
  // Calculate nice round intervals for y-axis labels
  const calculateNiceTicks = (min: number, max: number, numTicks: number = 5): number[] => {
    if (max === min) {
      return [Math.max(0, Math.floor(min)), Math.ceil(max)];
    }
    
    const range = max - min;
    const rawStep = range / (numTicks - 1);
    
    // Calculate magnitude and normalize step
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalizedStep = rawStep / magnitude;
    
    // Round to nice step (1, 2, 5, 10, 20, 50, 100, etc.)
    let niceStep: number;
    if (normalizedStep <= 1) {
      niceStep = 1;
    } else if (normalizedStep <= 2) {
      niceStep = 2;
    } else if (normalizedStep <= 5) {
      niceStep = 5;
    } else {
      niceStep = 10;
    }
    
    niceStep = niceStep * magnitude;
    
    // Calculate nice min (round down to nearest nice step)
    const niceMin = Math.floor(min / niceStep) * niceStep;
    // Calculate nice max (round up to nearest nice step)
    const niceMax = Math.ceil(max / niceStep) * niceStep;
    
    // Generate ticks
    const ticks: number[] = [];
    for (let tick = niceMin; tick <= niceMax; tick += niceStep) {
      ticks.push(tick);
    }
    
    return ticks;
  };
  
  // Check if vertical zoom is available (actual data range < 10)
  // We use the actual data range, not the display range, because nice ticks might round the range too much
  const isVerticallyZoomable =
    fixedYDomain == null
    && allDataPoints.length > 0
    && valueRange > 0
    && valueRange < 10;
  
  // Calculate display range based on zoom state
  let effectiveMin = minValue;
  let effectiveMax = maxValue;
  
  // Check if all values are non-negative (>= 0)
  const allValuesNonNegative = allDataPoints.length > 0 && minValue >= 0;

  if (fixedYDomain != null) {
    effectiveMin = fixedYDomain.min;
    effectiveMax = fixedYDomain.max;
  } else if (isVerticallyZoomed && isVerticallyZoomable) {
    // When zoomed in, add 10% padding above and below the actual data range
    const padding = valueRange * 0.1;
    effectiveMin = minValue - padding;
    effectiveMax = maxValue + padding;
    
    // For percentage data, cap the maximum at 100
    if (yAxisLabel.includes('%') || (maxValue <= 100 && minValue >= 0)) {
      effectiveMax = Math.min(effectiveMax, 100);
      // Also ensure minimum doesn't go below 0
      effectiveMin = Math.max(effectiveMin, 0);
    } else if (allValuesNonNegative) {
      // For non-negative data, ensure minimum doesn't go below 0
      effectiveMin = Math.max(effectiveMin, 0);
    }
  } else if (isVerticallyZoomable) {
    // When not zoomed but zoomable, use nice ticks that give a wider, standard range
    // For percentages, round to nearest 10 or 25
    if (yAxisLabel.includes('%') || (maxValue <= 100 && minValue >= 0)) {
      // For percentage-like data, use 0-100 as the default range
      effectiveMin = 0;
      effectiveMax = 100;
    } else {
      // For other data, use nice ticks that round to wider intervals
      const initialTicks = calculateNiceTicks(minValue, maxValue, 5);
      effectiveMin = initialTicks[0];
      effectiveMax = initialTicks[initialTicks.length - 1];
      // Expand the range to be at least 2x the data range for better context
      const expandedRange = Math.max((effectiveMax - effectiveMin), valueRange * 2);
      const center = (effectiveMin + effectiveMax) / 2;
      effectiveMin = center - expandedRange / 2;
      effectiveMax = center + expandedRange / 2;
      
      // If all values are non-negative, clamp minimum to 0
      if (allValuesNonNegative) {
        effectiveMin = Math.max(effectiveMin, 0);
      }
    }
  } else {
    // When not zoomable (normal case), ensure minimum is 0 if all values are non-negative
    if (allValuesNonNegative) {
      effectiveMin = 0;
    }
  }
  
  const yAxisTicks = calculateNiceTicks(effectiveMin, effectiveMax, 5);
  let displayMin = yAxisTicks[0];
  const displayMax = yAxisTicks[yAxisTicks.length - 1];
  
  // Final clamp: if all values are non-negative, ensure display minimum is at least 0
  if (allValuesNonNegative && displayMin < 0) {
    displayMin = 0;
    // Recalculate ticks with clamped minimum
    const clampedTicks = calculateNiceTicks(displayMin, displayMax, 5);
    displayMin = clampedTicks[0];
  }
  const displayRange = displayMax - displayMin || 1;
  
  // Handle vertical zoom toggle
  
  const effectiveChartHeight = chartHeight;
  // Dense tiles: keep side padding tight so the plot uses more horizontal space.
  // Extra horizontal room so middle-anchored edge labels (e.g. "Tue 28") do not clip.
  const rightPadding = dense ? 36 : 44;
  const leftPadding = hideYAxisLabels
    ? (dense ? 22 : 28)
    : (dense ? 44 : 74); // Dense + y-ticks: enough for numbers, less than full "Value" label gutter
  const bottomPadding = dense ? 40 : 60; // Dense: single-row x labels (no alternate up/down)
  const topPadding = dense ? 12 : 20;
  const xAxisLabelBelow = dense ? 22 : 40; // Offset from x-axis line to first label row
  const xAxisLabelAlt = dense ? 0 : 20; // Alternating row spacing (disabled when dense)
  const xAxisLabelPadding = 5; // Extra space at bottom for x-axis label
  const svgHeight = effectiveChartHeight + xAxisLabelPadding; // Total SVG height including space for x-axis label
  
  // Calculate chart width based on container and number of data points.
  // Never exceed the measured container — a 400px floor clipped half-width dashboard tiles.
  const chartWidth = containerWidth > 0
    ? containerWidth
    : (dense ? 120 : 400);
  
  // With one point, centre it; with 2+ points, space evenly across the plot width.
  const plotWidth = Math.max(chartWidth - leftPadding - rightPadding, 1);
  const pointSpacing = labels.length <= 1 ? 0 : plotWidth / (labels.length - 1);
  const singlePointOffset = labels.length === 1 ? plotWidth / 2 : 0;
  const xAt = (index: number) => leftPadding + singlePointOffset + index * pointSpacing;
  const selectedXIndex = selectedXLabel != null && selectedXLabel !== '' ? labels.indexOf(selectedXLabel) : -1;
  const selectedX = selectedXIndex >= 0 ? xAt(selectedXIndex) : null;
  
  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        if (w > 0) setContainerWidth(w);
      }
    };
    
    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, [data, series]); // Re-measure when data changes
  
  // Y coordinate of the zero line (for split green/red area fills)
  const zeroLineY = effectiveChartHeight - bottomPadding - ((0 - displayMin) / displayRange) * (effectiveChartHeight - topPadding - bottomPadding);

  // Generate path for line (and optional area paths: green above zero, red below zero)
  const generatePath = () => {
    if (chartSeries.length === 0) return [];

    return chartSeries.map(s => {
      const seriesPoints = labels.map((label, index) => {
        const point = s.data.find(p => p.label === label);
        const x = xAt(index);
        const value = point?.value ?? 0; // Default to 0 if no data for this label
        const ratio = (value - displayMin) / displayRange;
        const y = effectiveChartHeight - bottomPadding - (ratio * (effectiveChartHeight - topPadding - bottomPadding));
        return { x, y, value, label };
      });

      if (seriesPoints.length === 0) return { path: '', points: [], color: s.color, name: s.name, strokeWidth: s.strokeWidth ?? 2.5, areaPath: undefined, areaPathsAboveZero: [], areaPathsBelowZero: [] };

      let path = `M ${seriesPoints[0].x} ${seriesPoints[0].y}`;
      for (let i = 1; i < seriesPoints.length; i++) {
        path += ` L ${seriesPoints[i].x} ${seriesPoints[i].y}`;
      }

      const useGradient = gradientFillSeriesName != null && s.name === gradientFillSeriesName;
      let areaPath: string | undefined;
      const areaPathsAboveZero: string[] = [];
      const areaPathsBelowZero: string[] = [];

      if (useGradient && seriesPoints.length >= 2) {
        const getCross = (p1: { x: number; y: number; value: number }, p2: { x: number; y: number; value: number }) => {
          const v1 = p1.value;
          const v2 = p2.value;
          if (v1 === v2 || (v1 >= 0 && v2 >= 0) || (v1 <= 0 && v2 <= 0)) return null;
          const t = (0 - v1) / (v2 - v1);
          return { x: p1.x + t * (p2.x - p1.x), y: zeroLineY };
        };
        const buildAreaPath = (points: { x: number; y: number }[]) => {
          if (points.length === 0) return '';
          const first = points[0];
          const last = points[points.length - 1];
          let d = `M ${first.x} ${first.y}`;
          for (let i = 1; i < points.length; i++) d += ` L ${points[i].x} ${points[i].y}`;
          d += ` L ${last.x} ${zeroLineY} L ${first.x} ${zeroLineY} Z`;
          return d;
        };

        let i = 0;
        while (i < seriesPoints.length) {
          const v = seriesPoints[i].value;
          if (v >= 0) {
            const above: { x: number; y: number }[] = [];
            if (i > 0) {
              const cross = getCross(seriesPoints[i - 1], seriesPoints[i]);
              if (cross) above.push(cross);
            }
            while (i < seriesPoints.length && seriesPoints[i].value >= 0) {
              above.push({ x: seriesPoints[i].x, y: seriesPoints[i].y });
              i++;
            }
            if (i < seriesPoints.length) {
              const cross = getCross(seriesPoints[i - 1], seriesPoints[i]);
              if (cross) above.push(cross);
            }
            const segPath = buildAreaPath(above);
            if (segPath) areaPathsAboveZero.push(segPath);
          } else {
            const below: { x: number; y: number }[] = [];
            if (i > 0) {
              const cross = getCross(seriesPoints[i - 1], seriesPoints[i]);
              if (cross) below.push(cross);
            }
            while (i < seriesPoints.length && seriesPoints[i].value <= 0) {
              below.push({ x: seriesPoints[i].x, y: seriesPoints[i].y });
              i++;
            }
            if (i < seriesPoints.length) {
              const cross = getCross(seriesPoints[i - 1], seriesPoints[i]);
              if (cross) below.push(cross);
            }
            const segPath = buildAreaPath(below);
            if (segPath) areaPathsBelowZero.push(segPath);
          }
        }
      }

      return { path, points: seriesPoints, color: s.color, name: s.name, strokeWidth: s.strokeWidth ?? 2.5, areaPath, areaPathsAboveZero, areaPathsBelowZero };
    });
  };
  
  const generatedSeries = generatePath();

  // Which point indices have a visible x-axis label (for alternating value tooltip position)
  const getShowLabel = (index: number) => {
    if (xAxisLabelInterval != null) return index % xAxisLabelInterval === 0 || index === labels.length - 1;
    return labels.length <= 30 || index % Math.ceil(labels.length / 20) === 0 || index === labels.length - 1;
  };
  const visibleLabelIndices = labels.map((_, i) => i).filter(i => getShowLabel(i));
  
  return (
    <div className={`${dense
      ? 'bg-transparent p-0 border-0 shadow-none'
      : 'bg-card rounded-lg shadow-sm p-6 border border-border hover:border-success hover:shadow-lg hover:shadow-success/30'
    } transition-all flex flex-col w-full min-w-0 relative ${heightClass} overflow-visible`}
      onMouseLeave={() => setHoveredPoint(null)}
    >
      {title && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 w-full min-w-0">
          <h3 className="text-lg font-semibold text-white flex-1 min-w-0 pr-2">{title}</h3>
            {showWeekNavigation && (hasPreviousWeek || hasNextWeek) && (
            <div className="flex items-center gap-2 flex-shrink-0 min-w-0">
                {hasPreviousWeek && onPreviousWeek && (
                <div className="relative group flex-shrink-0">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={onPreviousWeek}
                    className="border border-transparent overflow-hidden max-w-28 hover:border-success hover:text-success hover:shadow-lg hover:shadow-success/50"
                    title={previousWeekLabel || 'Previous Week'}
                  >
                    <span className="block truncate">{previousWeekLabel || 'Previous Week'}</span>
                  </Button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-white text-accent text-xs font-medium rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-10">
                    Previous Week
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-white"></div>
                  </div>
                </div>
                )}
                {hasNextWeek && onNextWeek && (
                <div className="relative group flex-shrink-0">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={onNextWeek}
                    className="border border-transparent overflow-hidden max-w-28 hover:border-success hover:text-success hover:shadow-lg hover:shadow-success/50"
                    title={nextWeekLabel || 'Next Week'}
                  >
                    <span className="block truncate">{nextWeekLabel || 'Next Week'}</span>
                  </Button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-white text-accent text-xs font-medium rounded shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-10">
                    Next Week
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-white"></div>
                  </div>
                </div>
            )}
          </div>
          )}
        </div>
      )}
      
      <div 
        ref={containerRef}
        className="flex-1 min-h-0 min-w-0 w-full relative flex items-center justify-center"
        style={{ 
          overflow: 'visible',
        }}
      >
        <svg 
          ref={svgRef}
          width={chartWidth} 
          height={svgHeight} 
          style={{ 
            display: 'block',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
              <stop offset="0%" stopColor={STATUS_COLORS.success} stopOpacity={0.45} />
              <stop offset="100%" stopColor={STATUS_COLORS.success} stopOpacity={0.12} />
            </linearGradient>
            <linearGradient id={redGradientId} x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
              <stop offset="0%" stopColor={STATUS_COLORS.destructive} stopOpacity={0.22} />
              <stop offset="100%" stopColor={STATUS_COLORS.destructive} stopOpacity={0.55} />
            </linearGradient>
            {gradientFillSeriesName != null && chartSeries.map((s, seriesIndex) =>
              s.name === gradientFillSeriesName ? (
                <linearGradient key={seriesIndex} id={`${gradientId}-series-${seriesIndex}`} x1="0" y1="0" x2="0" y2="1" gradientUnits="objectBoundingBox">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.5} />
                  <stop offset="55%" stopColor={s.color} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                </linearGradient>
              ) : null
            )}
            {highlightLatestPointSeriesName && (
              <style>{`
                @keyframes latestPointFlicker-${latestPointPulseId} {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.45; }
                }
                .latest-point-pulse-${latestPointPulseId} {
                  animation: latestPointFlicker-${latestPointPulseId} 1.2s ease-in-out infinite;
                }
              `}</style>
            )}
          </defs>
          {/* Y-axis grid + optional numeric labels */}
          {yAxisTicks.map((tickValue, index) => {
            const ratio = (tickValue - displayMin) / displayRange;
            const y = effectiveChartHeight - (ratio * (effectiveChartHeight - topPadding - bottomPadding)) - bottomPadding;
            // Format the value nicely (remove unnecessary decimals)
            let formattedValue: string;
            if (formatYAxisAsK) {
              // Format as thousands (k)
              const valueInK = tickValue / 1000;
              formattedValue = valueInK % 1 === 0 ? `${valueInK}k` : `${valueInK.toFixed(1)}k`;
            } else {
              formattedValue = tickValue % 1 === 0 ? tickValue.toString() : tickValue.toFixed(1);
            }
            return (
              <g key={index}>
                <line
                  x1={leftPadding}
                  y1={y}
                  x2={chartWidth - rightPadding}
                  y2={y}
                  stroke="rgb(var(--secondary))"
                  strokeWidth={1}
                  strokeDasharray="2,2"
                />
                {!hideYAxisLabels && (
                  <text
                    x={leftPadding - 10}
                    y={y + 4}
                    fill="rgb(var(--muted-foreground))"
                    fontSize="12"
                    textAnchor="end"
                  >
                    {formattedValue}
                  </text>
                )}
              </g>
            );
          })}
          
          {/* Y-axis label — near top of axis when dense (dashboard tiles), otherwise centered */}
          {!hideYAxisLabels && yAxisLabel && (
            <text
              x={15}
              y={dense ? topPadding + 28 : effectiveChartHeight / 2}
              fill="rgb(var(--muted-foreground))"
              fontSize="12"
              textAnchor="middle"
              transform={`rotate(-90 15 ${dense ? topPadding + 28 : effectiveChartHeight / 2})`}
            >
              {yAxisLabel}
            </text>
          )}
          
          {/* Horizontal reference line (e.g. 70% target) */}
          {horizontalReferenceLine != null && horizontalReferenceLine.value >= displayMin && horizontalReferenceLine.value <= displayMax && (() => {
            const refValue = horizontalReferenceLine.value;
            const refRatio = (refValue - displayMin) / displayRange;
            const refY = effectiveChartHeight - bottomPadding - (refRatio * (effectiveChartHeight - topPadding - bottomPadding));
            const refColor = horizontalReferenceLine.color ?? STATUS_COLORS.success;
            const refDash = horizontalReferenceLine.strokeDasharray ?? '4,4';
            return (
              <g className="pointer-events-none">
                <line
                  x1={leftPadding}
                  y1={refY}
                  x2={chartWidth - rightPadding}
                  y2={refY}
                  stroke={refColor}
                  strokeWidth={1.5}
                  strokeDasharray={refDash}
                />
                {/* Green tick on y-axis at same level as reference line */}
                <foreignObject
                  x={leftPadding - 36}
                  y={refY - 12}
                  width={24}
                  height={24}
                  className="overflow-visible"
                >
                  <div style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircleCheckBig size={24} color={refColor} strokeWidth={2} />
                  </div>
                </foreignObject>
              </g>
            );
          })()}
          
          {/* Gradient area fills first (behind all lines): use series color when gradientFillSeriesName matches, else green above y=0, red below y=0 */}
          {generatedSeries.map((s, seriesIndex) => {
            const opacity = effectiveHighlightedSeriesIndex < 0 ? 1 : (isSeriesHighlighted(seriesIndex) ? 1 : 0.4);
            const useSeriesGradient = gradientFillSeriesName != null && s.name === gradientFillSeriesName;
            const gradientRef = useSeriesGradient ? `${gradientId}-series-${seriesIndex}` : gradientId;
            return (
              <g key={`areas-${seriesIndex}`}>
                {(s.areaPathsAboveZero ?? []).map((d, i) => (
                  <path key={`above-${i}`} d={d} fill={`url(#${gradientRef})`} stroke="none" style={{ opacity }} />
                ))}
                {(s.areaPathsBelowZero ?? []).map((d, i) => (
                  <path key={`below-${i}`} d={d} fill={`url(#${redGradientId})`} stroke="none" style={{ opacity }} />
                ))}
              </g>
            );
          })}
          {/* Grid lines and line path (single day → bar so one score is readable) */}
          {generatedSeries.map((s, seriesIndex) => {
            if (!s.path && s.points.length === 0) return null;
            const isSingleDay = labels.length === 1 && s.points.length === 1;
            if (isSingleDay) {
              const point = s.points[0];
              const isHovered = hoveredPoint?.seriesIndex === seriesIndex && hoveredPoint?.pointIndex === 0;
              const baselineY = effectiveChartHeight - bottomPadding;
              const barHeight = Math.max(2, baselineY - point.y);
              const barWidth = dense ? Math.min(56, Math.max(28, plotWidth * 0.28)) : Math.min(72, Math.max(36, plotWidth * 0.2));
              const barX = point.x - barWidth / 2;
              const valueY = Math.max(point.y - 10, 12);
              const formatted = formatValueAsInteger ? Math.round(point.value) : point.value.toFixed(1);
              return (
                <g key={seriesIndex}>
                  <rect
                    x={barX}
                    y={point.y}
                    width={barWidth}
                    height={barHeight}
                    rx={4}
                    ry={4}
                    fill={s.color}
                    fillOpacity={isHovered ? 0.95 : 0.75}
                    className="transition-all"
                    style={{
                      filter: isHovered ? `drop-shadow(0 0 8px ${s.color})` : 'none',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => {
                      if (containerRef.current?.parentElement) {
                        const parentRect = containerRef.current.parentElement.getBoundingClientRect();
                        const svgRect = svgRef.current?.getBoundingClientRect();
                        if (svgRect) {
                          setHoveredPoint({
                            seriesIndex,
                            pointIndex: 0,
                            x: svgRect.left - parentRect.left + point.x,
                            y: svgRect.top - parentRect.top + point.y,
                            value: point.value,
                            name: s.name,
                          });
                        }
                      }
                    }}
                    onMouseLeave={() => setHoveredPoint(null)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <text
                    x={point.x}
                    y={valueY}
                    fill="white"
                    fontSize={dense ? 11 : 12}
                    fontWeight="bold"
                    textAnchor="middle"
                    className="pointer-events-none"
                  >
                    {formatted}
                  </text>
                </g>
              );
            }

            return (
              <g key={seriesIndex}>
                {/* Line */}
                <path
                  d={s.path}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={s.strokeWidth ?? 2.5}
                  className="transition-all"
                  style={{
                    filter: isSeriesHighlighted(seriesIndex) ? `drop-shadow(0 0 8px ${s.color})` : 'none',
                    opacity: effectiveHighlightedSeriesIndex < 0 ? 1 : (isSeriesHighlighted(seriesIndex) ? 1 : 0.3),
                    pointerEvents: 'stroke',
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                
                {/* Data points: visible circles, or invisible hit areas when hideDataPoints (so hover dotted line still works) */}
                {s.points.map((point, pointIndex) => {
                  const isHovered = hoveredPoint?.seriesIndex === seriesIndex && hoveredPoint?.pointIndex === pointIndex;
                  const invisible = hideDataPoints;
                  // Alternate value tooltip up/down to avoid overlap with x-axis labels (which alternate down/up)
                  const visibleIndex = visibleLabelIndices.indexOf(pointIndex);
                  const labelIsInUpRow = visibleIndex >= 0 && visibleIndex % 2 === 1;
                  const valueYOffset = labelIsInUpRow ? 28 : 10; // put value higher when nearby x-label is in upper row
                  const valueY = Math.max(point.y - valueYOffset, 12);
                  return (
                    <g key={pointIndex}>
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r={isHovered ? 6 : 4}
                        fill={invisible ? 'transparent' : s.color}
                        stroke={invisible ? 'none' : undefined}
                        className="transition-all"
                        style={{
                          filter: !invisible && isHovered ? `drop-shadow(0 0 8px ${s.color})` : 'none',
                          cursor: 'pointer',
                          pointerEvents: 'all',
                        }}
                        onMouseEnter={() => {
                          if (containerRef.current && containerRef.current.parentElement) {
                            const parentRect = containerRef.current.parentElement.getBoundingClientRect();
                            const svgRect = svgRef.current?.getBoundingClientRect();
                            if (svgRect) {
                              const x = svgRect.left - parentRect.left + point.x;
                              const y = svgRect.top - parentRect.top + point.y;
                              setHoveredPoint({ seriesIndex, pointIndex, x, y, value: point.value, name: s.name });
                            }
                          }
                        }}
                        onMouseLeave={() => setHoveredPoint(null)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {isHovered && (
                        <text
                          x={point.x}
                          y={valueY}
                          fill="white"
                          fontSize="12"
                          fontWeight="bold"
                          textAnchor="middle"
                          className="pointer-events-none drop-shadow-glow-neutral-md"
                        >
                          {formatValueAsInteger ? Math.round(point.value) : point.value.toFixed(1)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {/* Flickering green point on the most recent date for the highlighted series (e.g. Net Profit) */}
          {highlightLatestPointSeriesName && (() => {
            const seriesIndex = chartSeries.findIndex(s => s.name === highlightLatestPointSeriesName);
            if (seriesIndex < 0) return null;
            const pts = generatedSeries[seriesIndex]?.points;
            if (!pts || pts.length === 0) return null;
            const last = pts[pts.length - 1];
            return (
              <g key="latest-point" className="pointer-events-none">
                <circle
                  cx={last.x}
                  cy={last.y}
                  r={8}
                  fill={STATUS_COLORS.success}
                  stroke="rgba(255,255,255,0.9)"
                  strokeWidth={2}
                  className={`latest-point-pulse-${latestPointPulseId}`}
                  style={{ filter: 'drop-shadow(0 0 6px rgba(34,197,94,0.8))' }}
                />
              </g>
            );
          })()}

          {/* Horizontal and vertical reference lines on hover; show x-value on x-axis only when this point has no visible label */}
          {hoveredPoint && generatedSeries[hoveredPoint.seriesIndex] && (() => {
            const point = generatedSeries[hoveredPoint.seriesIndex].points[hoveredPoint.pointIndex];
            const xAxisLineY = effectiveChartHeight - bottomPadding;
            const hoverLabelY = xAxisLineY + 40;
            const hasVisibleLabel = getShowLabel(hoveredPoint.pointIndex);
            return (
              <>
                <line
                  x1={leftPadding}
                  y1={point.y}
                  x2={point.x}
                  y2={point.y}
                  stroke="white"
                  strokeWidth={1.5}
                  strokeDasharray="4,4"
                  opacity={0.6}
                  className="pointer-events-none"
                />
                <line
                  x1={point.x}
                  y1={topPadding}
                  x2={point.x}
                  y2={xAxisLineY}
                  stroke="white"
                  strokeWidth={1.5}
                  strokeDasharray="4,4"
                  opacity={0.6}
                  className="pointer-events-none"
                />
                {!hasVisibleLabel && (
                  <text
                    x={point.x}
                    y={hoverLabelY}
                    fill={STATUS_COLORS.success}
                    fontSize="12"
                    fontWeight="bold"
                    textAnchor="middle"
                    className="pointer-events-none"
                    style={{ filter: 'drop-shadow(0 0 6px rgba(34,197,94,0.8))' }}
                  >
                    {formatXLabel ? formatXLabel(point.label) : point.label}
                  </text>
                )}
              </>
            );
          })()}

          {/* Vertical line at selected period (e.g. from Filter by Date) – white dotted line */}
          {selectedX != null && (
            <line
              x1={selectedX}
              y1={topPadding}
              x2={selectedX}
              y2={effectiveChartHeight - bottomPadding}
              stroke="white"
              strokeWidth={1.5}
              strokeDasharray="4,4"
              opacity={0.8}
              className="pointer-events-none"
            />
          )}
          
          {/* X-axis labels — single row when dense; alternate up/down otherwise to reduce crowding */}
          {(() => {
            const getShowLabel = (index: number) => {
              // Always show selected label, even if it wouldn't normally be shown
              if (selectedXIndex >= 0 && index === selectedXIndex) {
                return true;
              }
              if (xAxisLabelInterval != null) {
                return index % xAxisLabelInterval === 0 || index === labels.length - 1;
              }
              return labels.length <= 30 || index % Math.ceil(labels.length / 20) === 0 || index === labels.length - 1;
            };
            const displayLabel = (raw: string) => (formatXLabel ? formatXLabel(raw) : raw);
            const visibleLabels: { index: number; label: string }[] = [];
            labels.forEach((label, index) => {
              if (getShowLabel(index)) {
                visibleLabels.push({ index, label });
              }
            });
            
            return labels.map((label, index) => {
            const x = xAt(index);
              const showLabel = getShowLabel(index);
              // Find the position of this label in the visible labels array for proper alternation
              const visibleIndex = visibleLabels.findIndex(vl => vl.index === index);
              // Alternate label position based on visible index: even visible indices go down, odd visible indices go up
              // If selected label is not in visibleLabels (shouldn't happen now, but handle gracefully), use index % 2
              const isEven = visibleIndex >= 0 ? visibleIndex % 2 === 0 : index % 2 === 0;
            // Adjust label positions to accommodate weekday labels and x-axis line
            // Position labels below the x-axis line (which is at chartHeight - bottomPadding)
            // In SVG, y increases downward, so we add to position below the line
            const xAxisLineY = effectiveChartHeight - bottomPadding;
              const baseLabelY = xAxisLineY + xAxisLabelBelow;
              const labelY = dense ? baseLabelY : (isEven ? baseLabelY : baseLabelY - xAxisLabelAlt);
            const isHovered = hoveredPoint?.pointIndex === index;
            const isSelected = selectedXIndex >= 0 && index === selectedXIndex;
            // Always middle-anchor so labels sit on their ticks and stay evenly spaced.
            // (start/end on first/last made Control Centre tiles look clumped.)
            
            // Get the weekday for this label if available
            const dataPoint = allDataPoints.find(p => p.label === label);
            const weekday = dataPoint?.weekday;
            
            return (
              <g key={index}>
                {showLabel && (
                  <g
                    className="transition-all"
                    style={{
                      cursor: 'pointer',
                      pointerEvents: 'all',
                    }}
                    onMouseEnter={() => {
                      // Find the first series that has data for this label to show a tooltip
                      const seriesWithDataIndex = generatedSeries.findIndex(s => s.points[index]?.value !== undefined);
                      if (seriesWithDataIndex !== -1) {
                        const point = generatedSeries[seriesWithDataIndex].points[index];
                        const s = generatedSeries[seriesWithDataIndex];
                        if (containerRef.current && containerRef.current.parentElement) {
                          const parentRect = containerRef.current.parentElement.getBoundingClientRect();
                          const svgRect = svgRef.current?.getBoundingClientRect();
                          if (svgRect) {
                            const x = svgRect.left - parentRect.left + point.x;
                            const y = svgRect.top - parentRect.top + point.y;
                            setHoveredPoint({ seriesIndex: seriesWithDataIndex, pointIndex: index, x, y, value: point.value, name: s.name });
                          }
                        }
                      }
                    }}
                    onMouseLeave={() => setHoveredPoint(null)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Date label */}
                    <text
                      x={x}
                      y={labelY}
                      fill={isSelected || isHovered ? STATUS_COLORS.success : 'rgb(var(--muted-foreground))'}
                      fontSize="11"
                      fontWeight={isSelected || isHovered ? "bold" : "normal"}
                      textAnchor="middle"
                      style={{
                        filter: isSelected || isHovered ? 'drop-shadow(0 0 8px rgba(34,197,94,0.8)) drop-shadow(0 0 12px rgba(34,197,94,0.6))' : 'none',
                      }}
                    >
                      {displayLabel(label)}
                    </text>
                    {/* Weekday label (only for daily data) */}
                    {weekday && (
                      <text
                        x={x}
                        y={labelY + 14}
                        fill={isSelected || isHovered ? STATUS_COLORS.success : 'rgb(var(--muted-foreground))'}
                        fontSize="10"
                        fontWeight={isSelected || isHovered ? "bold" : "normal"}
                        textAnchor="middle"
                        style={{
                          filter: isSelected || isHovered ? 'drop-shadow(0 0 8px rgba(34,197,94,0.8)) drop-shadow(0 0 12px rgba(34,197,94,0.6))' : 'none',
                        }}
                      >
                        ({weekday})
                      </text>
                    )}
                  </g>
                )}
              </g>
            );
            });
          })()}
          
          {/* X-axis line */}
          <line
            x1={leftPadding}
            y1={effectiveChartHeight - bottomPadding}
            x2={chartWidth - rightPadding}
            y2={effectiveChartHeight - bottomPadding}
            stroke="rgb(var(--secondary))"
            strokeWidth={2}
            style={{ pointerEvents: 'none' }}
          />
          
          {/* X-axis label */}
          {xAxisLabel && (
            <text
              x={(leftPadding + chartWidth - rightPadding) / 2}
              y={effectiveChartHeight + 3}
              fill="rgb(var(--muted-foreground))"
              fontSize="12"
              textAnchor="middle"
            >
              {xAxisLabel}
            </text>
          )}
          
        </svg>
      </div>
      {/* Legend - same interactivity as hovering on lines */}
      {chartSeries.length > 1 && (
        <div className={`flex flex-wrap justify-center items-center gap-x-4 gap-y-2 ${dense ? 'mt-2' : 'mt-4'}`}>
          {chartSeries.map((s, index) => {
            const highlighted = isSeriesHighlighted(index);
            const firstPoint = generatedSeries[index]?.points[0];
            return (
              <div
                key={index}
                className="flex items-center gap-2 text-xs text-foreground cursor-pointer transition-opacity"
                style={{ opacity: effectiveHighlightedSeriesIndex < 0 ? 1 : (highlighted ? 1 : 0.5) }}
                onMouseEnter={() => {
                  if (firstPoint && containerRef.current?.parentElement && svgRef.current) {
                    const parentRect = containerRef.current.parentElement.getBoundingClientRect();
                    const svgRect = svgRef.current.getBoundingClientRect();
                    const x = svgRect.left - parentRect.left + firstPoint.x;
                    const y = svgRect.top - parentRect.top + firstPoint.y;
                    setHoveredPoint({ seriesIndex: index, pointIndex: 0, x, y, value: firstPoint.value, name: s.name });
                  } else {
                    setHoveredPoint({ seriesIndex: index, pointIndex: 0, x: 0, y: 0, value: 0, name: s.name });
                  }
                }}
                onMouseLeave={() => setHoveredPoint(null)}
              >
                <div 
                  className="w-3 h-3 rounded-full transition-all" 
                  style={{ 
                    backgroundColor: s.color,
                    opacity: effectiveHighlightedSeriesIndex < 0 ? 1 : (highlighted ? 1 : 0.4),
                    boxShadow: highlighted ? `0 0 6px ${s.color}` : 'none'
                  }}
                />
                <span>{s.name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
